import React from 'react';

/**
 * SafeError - Safely renders error messages without crashing on objects
 * Handles various error formats:
 * - String messages
 * - Error objects with .message
 * - Pydantic validation errors [{type, loc, msg}]
 * - FastAPI detail objects
 */
export const SafeError = ({ error, className = '' }) => {
  if (!error) return null;

  const errorMessage = extractErrorMessage(error);
  
  if (!errorMessage) return null;

  return (
    <p className={className} data-testid="error-message">
      {errorMessage}
    </p>
  );
};

/**
 * Extracts a safe string message from various error formats
 */
export const extractErrorMessage = (error) => {
  // Already a string
  if (typeof error === 'string') {
    return error;
  }

  // Null or undefined
  if (!error) {
    return null;
  }

  // Standard Error object
  if (error instanceof Error) {
    return error.message;
  }

  // Pydantic validation error array: [{type, loc, msg, input, ctx, url}]
  if (Array.isArray(error)) {
    // Extract messages from validation errors
    const messages = error
      .map(e => {
        if (typeof e === 'string') return e;
        if (e?.msg) return e.msg;
        if (e?.message) return e.message;
        return null;
      })
      .filter(Boolean);
    
    return messages.length > 0 ? messages.join('. ') : 'Validation error';
  }

  // Object with message property
  if (error.message) {
    return typeof error.message === 'string' ? error.message : 'An error occurred';
  }

  // Object with detail property (FastAPI)
  if (error.detail) {
    // Detail can be string or array of validation errors
    if (typeof error.detail === 'string') {
      return error.detail;
    }
    if (Array.isArray(error.detail)) {
      return extractErrorMessage(error.detail);
    }
    if (error.detail.msg) {
      return error.detail.msg;
    }
  }

  // Object with msg property (Pydantic)
  if (error.msg) {
    return error.msg;
  }

  // Last resort - try to stringify, but catch any errors
  try {
    const str = JSON.stringify(error);
    // Don't return huge JSON blobs
    if (str.length > 200) {
      return 'An unexpected error occurred';
    }
    return str;
  } catch {
    return 'An unexpected error occurred';
  }
};

/**
 * Extracts error message from axios error response
 */
export const extractAxiosError = (err) => {
  // Get the response data detail
  const detail = err?.response?.data?.detail;
  
  if (detail) {
    return extractErrorMessage(detail);
  }

  // Fallback to error message
  if (err?.message) {
    return err.message;
  }

  return 'An unexpected error occurred';
};

export default SafeError;
